import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const REASONS = [
  ["holiday", "Holiday"],
  ["illness", "Illness"],
  ["temporary_pause", "Temporary pause"],
  ["new_work_pattern", "New work pattern"],
  ["reduced_frequency", "Fewer lessons for now"],
  ["stopping", "Stopping lessons"],
  ["service_issue", "Something about the service"],
  ["other", "Something else"],
];

const TEMPORARY = new Set(["holiday", "illness", "temporary_pause"]);

export default function PupilRhythmReviewCard({ pupilId }) {
  const [review, setReview] = useState(null);
  const [reason, setReason] = useState("");
  const [detail, setDetail] = useState("");
  const [temporaryUntil, setTemporaryUntil] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!pupilId) return;
    try {
      const { data } = await axios.get(
        `${API}/api/bookings/pupil-rhythm/reviews`,
        { withCredentials: true }
      );
      const match = (data?.reviews || []).find(
        (row) => String(row?.pupil_id || "") === String(pupilId)
      );
      setReview(match || null);
    } catch {
      // This is a support prompt, never a blocker for the pupil profile.
      setReview(null);
    }
  }, [pupilId]);

  useEffect(() => { load(); }, [load]);

  if (!review) return null;

  const temporary = TEMPORARY.has(reason);

  const save = async () => {
    if (!reason) {
      toast.error("Choose what changed first.");
      return;
    }
    if (temporary && !temporaryUntil) {
      toast.error("Add when the temporary change is expected to end.");
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/api/bookings/pupil-rhythm/response`,
        {
          pupil_id: pupilId,
          reason_code: reason,
          detail: detail.trim(),
          temporary_until: temporary ? temporaryUntil : null,
        },
        { withCredentials: true }
      );
      setReview(null);
      toast.success("Recorded. No pupil message or advertising was sent.");
    } catch (err) {
      const detailValue = err?.response?.data?.detail;
      toast.error(
        typeof detailValue === "string"
          ? detailValue
          : detailValue?.message || "Could not record that answer."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      data-testid="pupil-rhythm-review"
      className="rounded-2xl border border-amber-200 dark:border-amber-900/50 bg-amber-50/70 dark:bg-amber-950/20 p-4"
    >
      <p className="text-xs font-extrabold uppercase tracking-wide text-amber-700 dark:text-amber-300">
        Booking pattern check
      </p>
      <p
        data-testid="pupil-rhythm-question"
        className="mt-2 text-sm font-semibold text-slate-800 dark:text-slate-100"
      >
        {review.question}
      </p>

      <label className="block mt-3">
        <span className="text-xs font-bold text-slate-500 dark:text-slate-400">
          What changed?
        </span>
        <select
          data-testid="pupil-rhythm-reason"
          value={reason}
          onChange={(event) => {
            setReason(event.target.value);
            if (!TEMPORARY.has(event.target.value)) setTemporaryUntil("");
          }}
          className="mt-1 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2.5 text-sm"
        >
          <option value="">Choose one</option>
          {REASONS.map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </label>

      {temporary && (
        <label className="block mt-3">
          <span className="text-xs font-bold text-slate-500 dark:text-slate-400">
            Until when?
          </span>
          <input
            data-testid="pupil-rhythm-until"
            type="date"
            value={temporaryUntil}
            onChange={(event) => setTemporaryUntil(event.target.value)}
            className="mt-1 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2.5 text-sm"
          />
        </label>
      )}

      <label className="block mt-3">
        <span className="text-xs font-bold text-slate-500 dark:text-slate-400">
          Anything useful to remember? <span className="font-normal">(optional)</span>
        </span>
        <textarea
          data-testid="pupil-rhythm-detail"
          value={detail}
          onChange={(event) => setDetail(event.target.value)}
          rows={2}
          maxLength={500}
          className="mt-1 w-full resize-none rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2.5 text-sm"
        />
      </label>

      <button
        type="button"
        data-testid="pupil-rhythm-save"
        disabled={saving}
        onClick={save}
        className="mt-3 w-full h-10 rounded-xl bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-sm font-extrabold disabled:opacity-60"
      >
        {saving ? "Recording…" : "Record answer"}
      </button>

      <p className="mt-2 text-[11px] text-slate-500 dark:text-slate-400">
        This updates support and capacity forecasting only. It does not message the pupil or start advertising.
      </p>
    </section>
  );
}
